import {
  Button,
  FileButton,
  Group,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import Drawer from '../drawer/drawer';

import {
  ImportSubmissionClause,
  ImportSubmissionVolumeType,
} from '@data-access';

interface OsImportFormValues {
  clause: ImportSubmissionClause | null;
  volume_type: ImportSubmissionVolumeType | null;
  data_file: File | null;
  fields_file: File | null;
}

export interface OsImportFormSubmitValues {
  clause: ImportSubmissionClause;
  volume_type: ImportSubmissionVolumeType;
  data_file: File;
  fields_file?: File | null;
}

interface OsImportFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: OsImportFormSubmitValues) => void;
  loading?: boolean;
}

export function OsImportForm({
  opened,
  onClose,
  onSubmit,
  loading,
}: OsImportFormProps) {
  const { t } = useTranslation();
  const form = useForm<OsImportFormValues>({
    initialValues: {
      clause: null,
      volume_type: null,
      data_file: null,
      fields_file: null,
    },
    validate: {
      clause: isNotEmpty(t('form.required')),
      volume_type: isNotEmpty(t('form.required')),
      data_file: isNotEmpty(t('form.required')),
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = (values: OsImportFormValues) => {
    onSubmit({
      clause: values.clause as ImportSubmissionClause,
      volume_type: values.volume_type as ImportSubmissionVolumeType,
      data_file: values.data_file!,
      fields_file: values.fields_file,
    });
    form.reset();
    onClose();
  };

  return (
    <Drawer
      onClose={handleClose}
      opened={opened}
      position="right"
      size="auto"
      title={t('connectivity.os-import-title')}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md" mt="sm">
          <Select
            data={[
              { label: t('connectivity.clause-gp'), value: 'gp' },
              { label: t('connectivity.clause-non-gp'), value: 'new' },
            ]}
            label={t('connectivity.clause-label')}
            onChange={(value) => {
              form.setFieldValue('clause', value as ImportSubmissionClause | null);
            }}
            value={form.values.clause}
          />
          {form.errors.clause ? (
            <Text c="red" size="sm">
              {form.errors.clause}
            </Text>
          ) : null}

          <Select
            data={[
              { label: t('connectivity.volume-saipol'), value: 'saipol' },
              { label: t('connectivity.volume-non-saipol'), value: 'non_saipol' },
            ]}
            label={t('connectivity.volume-label')}
            onChange={(value) => {
              form.setFieldValue(
                'volume_type',
                value as ImportSubmissionVolumeType | null,
              );
            }}
            value={form.values.volume_type}
          />
          {form.errors.volume_type ? (
            <Text c="red" size="sm">
              {form.errors.volume_type}
            </Text>
          ) : null}

          <div>
            <FileButton
              accept=".xlsx,.xls"
              onChange={(file) => {
                form.setFieldValue('data_file', file);
              }}
            >
              {(props) => (
                <Button {...props} fullWidth>
                  {t('common.selectionner-un-fichier')} - {t('connectivity.data-file')}
                </Button>
              )}
            </FileButton>
            {form.values.data_file ? (
              <Text mt="xs" size="sm">
                {t('field.form.fileSelected')} {form.values.data_file.name}
              </Text>
            ) : null}
            {form.errors.data_file ? (
              <Text c="red" mt="xs" size="sm">
                {form.errors.data_file}
              </Text>
            ) : null}
          </div>

          <div>
            <FileButton
              accept=".zip,.rar,.7z,.gz,.xml"
              onChange={(file) => {
                form.setFieldValue('fields_file', file);
              }}
            >
              {(props) => (
                <Button {...props} fullWidth>
                  {t('common.selectionner-un-fichier')} -{' '}
                  {t('connectivity.fields-file-xml-optional')}
                </Button>
              )}
            </FileButton>
            {form.values.fields_file ? (
              <Text mt="xs" size="sm">
                {t('field.form.fileSelected')} {form.values.fields_file.name}
              </Text>
            ) : null}
            {form.errors.fields_file ? (
              <Text c="red" mt="xs" size="sm">
                {form.errors.fields_file}
              </Text>
            ) : null}
          </div>
        </Stack>

        <Group mt="xl">
          <Button loading={loading} type="submit">
            {t('connectivity.submit-for-review')}
          </Button>
          <Button color="red" onClick={handleClose} type="button">
            {t('common.cancel')}
          </Button>
        </Group>
      </form>
    </Drawer>
  );
}

export default OsImportForm;
